import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Mail, RefreshCw } from 'lucide-react';

export function VerifyEmail() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    // Check if this is a redirect from successful verification
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    const messageParam = urlParams.get('message');

    if (verified === 'true' && messageParam) {
      setStatus('success');
      setMessage(decodeURIComponent(messageParam));
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // If no token in URL, show error
    const token = urlParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Token de verificación no encontrado');
      return;
    }

    // Verify the email
    verifyEmail(token);
  }, []);

  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`/api/auth/verify-email?token=${token}`);
      
      if (response.ok) {
        setStatus('success');
        setMessage('¡Email verificado exitosamente! Ya puedes iniciar sesión.');
      } else {
        const errorData = await response.json();
        setStatus('error');
        setMessage(errorData.message || 'Error al verificar el email');
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      setStatus('error');
      setMessage('Error de conexión. Por favor, intenta de nuevo.');
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: '' }), // We'll need to get this from somewhere
      });

      if (response.ok) {
        setMessage('Email de verificación reenviado. Revisa tu bandeja de entrada.');
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Error al reenviar el email');
      }
    } catch (error) {
      console.error('Error resending verification:', error);
      setMessage('Error de conexión. Por favor, intenta de nuevo.');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    setLocation('/login');
  };

  const handleGoToHome = () => {
    setLocation('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            {status === 'loading' && (
              <RefreshCw className="w-8 h-8 text-white animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="w-8 h-8 text-white" />
            )}
            {(status === 'error' || status === 'expired') && (
              <XCircle className="w-8 h-8 text-white" />
            )}
          </div>
          
          <CardTitle className="text-2xl font-bold">
            {status === 'loading' && 'Verificando Email...'}
            {status === 'success' && '¡Email Verificado!'}
            {(status === 'error' || status === 'expired') && 'Error de Verificación'}
          </CardTitle>
          
          <CardDescription>
            {status === 'loading' && 'Por favor espera mientras verificamos tu email...'}
            {status === 'success' && 'Tu cuenta ha sido verificada exitosamente'}
            {(status === 'error' || status === 'expired') && 'No pudimos verificar tu email'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {message && (
            <Alert className={status === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
              <AlertDescription className="text-center">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {status === 'success' && (
            <div className="space-y-3">
              <Button 
                onClick={handleGoToLogin}
                className="w-full"
              >
                Ir a Iniciar Sesión
              </Button>
              <Button 
                onClick={handleGoToHome}
                variant="outline"
                className="w-full"
              >
                Ir al Inicio
              </Button>
            </div>
          )}

          {(status === 'error' || status === 'expired') && (
            <div className="space-y-3">
              <div className="text-center text-sm text-muted-foreground">
                <Mail className="w-4 h-4 inline mr-1" />
                ¿No recibiste el email de verificación?
              </div>
              
              <Button 
                onClick={handleResendVerification}
                disabled={isResending}
                variant="outline"
                className="w-full"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Reenviando...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Reenviar Email
                  </>
                )}
              </Button>
              
              <Button 
                onClick={handleGoToLogin}
                className="w-full"
              >
                Ir a Iniciar Sesión
              </Button>
              
              <Button 
                onClick={handleGoToHome}
                variant="ghost"
                className="w-full"
              >
                Volver al Inicio
              </Button>
            </div>
          )}

          {status === 'loading' && (
            <div className="text-center">
              <div className="text-sm text-muted-foreground">
                Esto puede tomar unos segundos...
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
